import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import BinaryTreeEngine from "../TreeEngine/BinaryTreeEngine";
import BSTEngine from "../TreeEngine/BSTEngine";
import styles from "../Tree.module.css";

const CompareTreeWrapper: React.FC = () => {
  const navigate = useNavigate();
  const [nodeValues, setNodeValues] = useState<number[]>([]);
  const [traversal, setTraversal] = useState<string | null>(null);
  const [insertValue, setInsertValue] = useState<number | "">("");
  const [deleteValue, setDeleteValue] = useState<number | "">("");
  const [highlightLine, setHighlightLine] = useState<number | null>(null);
  const [highlightedNodesBT, setHighlightedNodesBT] = useState<number[]>([]);
  const [highlightedNodesBST, setHighlightedNodesBST] = useState<number[]>([]);
  const [traversalComplete, setTraversalComplete] = useState<boolean>(false);
  const [resetTrigger, setResetTrigger] = useState<boolean>(false);
  const [generateTrigger, setGenerateTrigger] = useState<number>(0);

  const handleGenerate = () => {
    const values = Array.from({ length: 7 }, () => Math.floor(Math.random() * 90) + 10);
    setNodeValues(values);
    setTraversal(null);
    setHighlightLine(null);
    setHighlightedNodesBT([]);
    setHighlightedNodesBST([]);
    setGenerateTrigger((prev) => prev + 1);
  };

  const handleInsert = () => {
    if (insertValue === "" || isNaN(Number(insertValue))) return;
    setNodeValues((prev) => [...prev, Number(insertValue)]);
    setInsertValue("");
    setTraversal(null);
    setGenerateTrigger((prev) => prev + 1);
  };

  const handleDelete = () => {
    if (deleteValue === "" || isNaN(Number(deleteValue))) return;
    setNodeValues((prev) => prev.filter((val) => val !== Number(deleteValue)));
    setDeleteValue("");
    setTraversal(null);
    setGenerateTrigger((prev) => prev + 1);
  };

  const handleTraversal = (type: string) => {
    setTraversal(type);
    setHighlightLine(null);
    setHighlightedNodesBT([]);
    setHighlightedNodesBST([]);
  };

  const handleReset = () => {
    setTraversal(null);
    setHighlightLine(null);
    setHighlightedNodesBT([]);
    setHighlightedNodesBST([]);
    setResetTrigger((prev) => !prev);
  };

  return (
    <div className={styles.treeContainer}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>
        ← Return
      </button>

      <h1 className={styles.title}>Compare Trees: Binary Tree vs Binary Search Tree</h1>

      <p className={styles.description}>
        Imagine organizing information in two different ways: one that simply fills space, and another that follows a precise set of rules. 
        A <b>Binary Tree</b> grows by shape — left to right, top to bottom — like stacking boxes row by row. 
        A <b></b>Binary Search Tree (BST), on the other hand, grows by value — smaller numbers to the left, larger to the right — like a librarian shelving books in order.

        Both start with the same pieces, but their internal structure changes how quickly you can find, insert, or remove things. This comparison shows how organization shapes efficiency — two trees, same data, very different behavior.
      </p>

        <div className={styles.treesCompareNote}>
        <h3>Key Observations:</h3>
        <ul>
          <li>
            Both trees start with the <strong>same numbers</strong>, but their structures differ.
          </li>
          <li>
            <strong>Binary Tree:</strong> fills by shape (top-down, left-right).
          </li>
          <li>
            <strong>BST:</strong> inserts by value (smaller → left, larger → right).
          </li>
          <li>
            When traversed <em>inorder</em>, the BST always produces a sorted list.
          </li>
        </ul>
      </div>

      <div className={styles.controls}>

        <div className={styles.inputGroup}>
          <input
            type="number"
            placeholder="Insert value"
            value={insertValue}
            onChange={(e) =>
              setInsertValue(e.target.value === "" ? "" : Number(e.target.value))
            }
            className={styles.inputField}
          />
          <button onClick={handleInsert}>Insert</button>
        </div>

        <div className={styles.inputGroup}>
          <input
            type="number"
            placeholder="Delete value"
            value={deleteValue}
            onChange={(e) =>
              setDeleteValue(e.target.value === "" ? "" : Number(e.target.value))
            }
            className={styles.inputField}
          />
          <button onClick={handleDelete}>Delete</button>
        </div>
        <button onClick={handleGenerate}>Generate Trees</button>
        <button onClick={() => handleTraversal("preorder")}>Preorder</button>
        <button onClick={() => handleTraversal("inorder")}>Inorder</button>
        <button onClick={() => handleTraversal("postorder")}>Postorder</button>
        <button onClick={() => handleTraversal("levelorder")}>Level Order</button>
        <button onClick={handleReset}>Reset</button>
      </div>

      <div className={styles.treesCompareGrid}>
      <div className={styles.treesCompareColumn}>
          <h2 className={styles.treesCompareHeader}>Binary Tree</h2>
          <BinaryTreeEngine
            externalNodes={nodeValues}
            traversal={traversal}
            setHighlightLine={setHighlightLine}
            highlightedNodes={highlightedNodesBT}
            setHighlightedNodes={setHighlightedNodesBT}
            resetTrigger={generateTrigger}
          />

          {traversal && (
            <div className={styles.resultBox}>
              <h3>Binary Tree Result ({traversal}):</h3>
              <p>
                {highlightedNodesBT.length === 0
                  ? "No traversal yet."
                  : highlightedNodesBT.join(" → ")}
              </p>
            </div>
          )}
        </div>

        <div className={styles.treesCompareColumn}>
          <h2 className={styles.treesCompareHeader}>Binary Search Tree (BST)</h2>
          <BSTEngine
            externalNodes={nodeValues}
            generateTrigger={generateTrigger}
            traversal={traversal}
            setHighlightLine={setHighlightLine}
            highlightedNodes={highlightedNodesBST}
            setHighlightedNodes={setHighlightedNodesBST}
            resetTrigger={resetTrigger}
            setTraversalComplete={setTraversalComplete}
          />
              {traversal && (
          <div className={styles.resultBox}>
            <h3>BST Result ({traversal}):</h3>
            <p>
              {highlightedNodesBST.length === 0
                ? "No traversal yet."
                : highlightedNodesBST.join(" → ")}
            </p>
          </div>
           )}
        </div>
      </div>
    </div>
  );
};

export default CompareTreeWrapper;
